/**
 * When someone cannot open something (18 Oct 2026): "No access yet" for people with no apps at all (new joiners from
 * the HR data), and "Locked" for an app they do not have. Both let them ask for access; that app's admins see the
 * request in Settings.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AppKey } from '../lib/api';
import { useMe, useSignOut } from '../lib/session';
import { TfMark } from '../taskforce/brand';
import { AppShell } from '../components/AppChrome';
import { Icon } from '../taskforce/ui';
import '../styles/taskforce.css';

export const APP_LABEL: Record<AppKey, string> = {
  taskforce: 'Task Force',
  attendance: 'Attendance',
  clickup: 'Click Up report',
};
const APP_LINE: Record<AppKey, string> = {
  taskforce: 'Tasks, spaces and workflows for your team.',
  attendance: 'Your attendance record (or your team’s).',
  clickup: 'Who is working on what, from ClickUp.',
};

/** Open requests of mine, and a way to make one. */
function useRequests() {
  const qc = useQueryClient();
  const mine = useQuery({
    queryKey: ['access', 'mine'],
    queryFn: () => api<Array<{ app: AppKey; at: string }>>('/api/access/requests/mine'),
  });
  const ask = async (app: AppKey) => {
    try {
      await api('/api/access/requests', { method: 'POST', json: { app } });
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
    }
    await qc.invalidateQueries({ queryKey: ['access', 'mine'] });
  };
  const asked = (app: AppKey) => !!mine.data?.some((r) => r.app === app);
  return { ask, asked, ready: !mine.isLoading };
}

function RequestButton({ app }: { app: AppKey }) {
  const r = useRequests();
  if (!r.ready) return null;
  return r.asked(app) ? (
    <span className="tfna-asked" role="status">
      Requested · your admins will see it
    </span>
  ) : (
    <button type="button" className="tfbtn primary" onClick={() => void r.ask(app)}>
      Request access
    </button>
  );
}

/** Nothing to open yet. */
export function NoAccess() {
  const me = useMe().data;
  const signOut = useSignOut();
  const navigate = useNavigate();
  const first = me?.displayName.split(' ')[0] ?? '';
  return (
    <AppShell area="taskforce">
      <div className="tfna" role="region" aria-labelledby="naTitle">
        <div className="tfna-card">
          <TfMark size={54} />
          <h1 id="naTitle">Welcome{first ? `, ${first}` : ''}</h1>
          <p className="tfna-lead">
            You don’t have access to anything yet. Contact your administrator to give you roles and permissions, or ask
            for the apps you need below.
          </p>
          <ul className="tfna-apps">
            {(['taskforce', 'attendance', 'clickup'] as const).map((app) => (
              <li key={app}>
                <span className="tfna-lock" aria-hidden="true">
                  <Icon n="lock" size={16} />
                </span>
                <span className="tfna-app">
                  <b>{APP_LABEL[app]}</b>
                  <span>{APP_LINE[app]}</span>
                </span>
                <RequestButton app={app} />
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="tflink"
            onClick={async () => {
              await signOut();
              navigate('/sign-in', { replace: true });
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </AppShell>
  );
}

/** An app they cannot open. */
export function Locked({ app }: { app: AppKey }) {
  const me = useMe().data;
  const navigate = useNavigate();
  const home = me?.access?.home ?? '/no-access';
  return (
    <AppShell area={app}>
      <div className="tfna" role="region" aria-labelledby="lkTitle">
        <div className="tfna-card">
          <span className="tfna-biglock" aria-hidden="true">
            <Icon n="lock" size={30} />
          </span>
          <h1 id="lkTitle">You don’t have access to {APP_LABEL[app]}</h1>
          <p className="tfna-lead">Contact your administrator for access, or ask for it here.</p>
          <div className="tfna-acts">
            <RequestButton app={app} />
            <button type="button" className="tfbtn" onClick={() => navigate(home)}>
              Go back
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
